import { CSSResultGroup, html, PropertyValues } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { BooleanFormElement } from 'elements/_shared/formElement.js'
import { hasDefaultSlot } from 'elements/_shared/hasSlot.js'
import { style } from './radio-button.style.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'

let radioButtonIdCounter = 0
const nextRadioButtonId = () => `cse-radio-button-${++radioButtonIdCounter}`

@customElement('cse-radio-button')
export class CSERadioButton extends BooleanFormElement {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: String, attribute: 'id' })
  _id = ''

  private readonly generatedId = nextRadioButtonId()

  private get inputId() {
    return this._id || this.generatedId
  }

  @property({ type: String })
  name = '' // not used, but must be implemented (inherited abstract member)

  @property({ type: String, reflect: true })
  value = ''

  @property({ type: Boolean })
  required = false

  @property({ type: Boolean })
  disabled = false

  @property({ type: Boolean, reflect: true })
  checked = false

  @query('input')
  radio!: HTMLInputElement

  private handleContainerClick = (event: Event) => {
    if (this.disabled) {
      return
    }

    const target = event.target
    if (target instanceof HTMLInputElement || target instanceof HTMLLabelElement) {
      return
    }

    if (target instanceof HTMLElement && target.closest('label')) {
      return
    }

    this.focus()
    this.setChecked(true)
  }

  private handleInputChange = (event: Event) => {
    const target = event.target
    if (!(target instanceof HTMLInputElement)) {
      return
    }

    this.setChecked(target.checked)
  }

  private handleSpacebarPressed = (event: KeyboardEvent): void => {
    if (event.key === ' ') {
      this.setChecked(true)
    }
  }

  private setChecked = (state: boolean): void => {
    this.checked = state
    if (this.checked) {
      this.emit(this.value)
    }
  }

  override connectedCallback() {
    super.connectedCallback()

    this.addEventListener('click', this.handleContainerClick)
    this.addEventListener('keyup', this.handleSpacebarPressed)
  }

  override disconnectedCallback() {
    super.disconnectedCallback()

    this.removeEventListener('click', this.handleContainerClick)
    this.removeEventListener('keyup', this.handleSpacebarPressed)
  }

  protected override updated(changedProperties: PropertyValues) {
    super.updated(changedProperties)

    if (changedProperties.has('checked')) {
      this.radio.checked = this.checked
    }
  }

  override focus() {
    this.radio.focus()
  }

  override render() {
    return html`
      <div
        class=${classMap({
          'cse-radio-button': true,
          checked: this.checked,
          disabled: this.disabled,
        })}
      >
        <input
          type="radio"
          id="${this.inputId}"
          name="${this.name}"
          value="${this.value}"
          ?required="${this.required}"
          ?disabled="${this.disabled}"
          ?checked="${this.checked}"
          ?autofocus="${this.autofocus}"
          @change="${this.handleInputChange}"
        />

        <div class="radio-button" aria-hidden="true">
          ${this.checked ? html` <span class="checkmark"></span> ` : null}
        </div>

        <label class="label" for="${this.inputId}" ?hidden="${!hasDefaultSlot(this)}">
          <slot></slot>
        </label>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cse-radio-button': CSERadioButton
  }
}
